import { StatsigClient } from "@statsig/js-client";

import { logger } from "src/utility/logger";

let statsigClient: StatsigClient | undefined;

const MAX_QUEUED_LOG_EVENTS = 500;
const queuedLogEvents: Parameters<StatsigClient["logEvent"]>[] = [];
let hasWarnedQueueFull = false;

const queueLogEvent: StatsigClient["logEvent"] = (...args) => {
  if (queuedLogEvents.length >= MAX_QUEUED_LOG_EVENTS) {
    if (!hasWarnedQueueFull) {
      hasWarnedQueueFull = true;
      logger.warn({
        message: `Event queue exceeded max ${MAX_QUEUED_LOG_EVENTS}, dropping event`,
      });
    }
    return;
  }
  queuedLogEvents.push(args);
};

// Keep early references usable after the client is set.
const statsigClientProxy = new Proxy<StatsigClient>(Object.create(null), {
  // Runs on every property read, e.g. `getStatsigClient().logEvent`, and
  // decides what the caller gets back
  get: (_target, prop) => {
    // Client is set: hand back its real property. Methods are bound so the
    // SDK's `this` is the client, not this proxy
    if (statsigClient) {
      const value = Reflect.get(statsigClient, prop);
      return typeof value === "function" ? value.bind(statsigClient) : value;
    }
    // Not thenable, so `await`ing the proxy resolves instead of hanging, and
    // coercion or inspection via symbols (e.g. Symbol.toPrimitive) is a no-op
    if (prop === "then" || typeof prop === "symbol") {
      return undefined;
    }
    // Queue events until setStatsigClient replays them
    if (prop === "logEvent") {
      return queueLogEvent;
    }
    // Any other method is a no-op until the client is set
    return () =>
      logger.info({
        message: "Statsig client not initialized, ignoring call",
        data: { method: String(prop) },
      });
  },
});

const getStatsigClient = () => statsigClientProxy;

const setStatsigClient = (client: StatsigClient) => {
  statsigClient = client;
  queuedLogEvents.splice(0).forEach((args) => client.logEvent(...args));
};

export { getStatsigClient, setStatsigClient };
